import { useCallback } from "react";
import { toast } from "sonner";

import { api, queryClient, uploadDocument, type DocumentOut, type JobOut } from "@/lib/api";
import { keys } from "@/hooks/queries";
import type { UploadItem } from "@/lib/dropzone-entries";
import { useActivity } from "@/stores/activity";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";

// Mirrors the backend parser registry (ingestion/parsers). Documents, office
// files, web pages, and source-code / data text.
export const ACCEPTED = [
  ".pdf", ".md", ".markdown", ".txt",
  ".docx", ".pptx",
  ".html", ".htm", ".xhtml",
  // source code
  ".py", ".pyi", ".js", ".jsx", ".mjs", ".cjs", ".ts", ".tsx", ".java", ".kt",
  ".kts", ".scala", ".c", ".h", ".cpp", ".cc", ".cxx", ".hpp", ".cs", ".go",
  ".rs", ".swift", ".rb", ".php", ".pl", ".lua", ".r", ".sh", ".bash", ".zsh",
  ".ps1", ".sql", ".dart", ".ex", ".exs", ".hs", ".clj", ".vue", ".svelte",
  // config / data
  ".json", ".jsonc", ".yaml", ".yml", ".toml", ".ini", ".cfg", ".env",
  ".xml", ".csv", ".tsv", ".tex", ".gradle",
];

/** A single comma-joined string for <input accept="…">. */
export const ACCEPT_ATTR = ACCEPTED.join(",");

/** Track a backend job to completion, mirroring ingest stage_status live. */
export async function trackJob(jobId: string, docId?: string): Promise<JobOut> {
  const { upsert } = useActivity.getState();
  for (;;) {
    const job = (await api.getJob(jobId)) as unknown as JobOut;
    let stages: Record<string, string> | undefined;
    if (docId && (job.status === "running" || job.status === "queued")) {
      try {
        const doc = (await api.getDocument(docId)) as unknown as DocumentOut;
        stages = doc.stage_status;
      } catch {
        /* document may not be visible yet */
      }
    }
    upsert({ id: jobId, status: job.status, stages, error: job.error, result: job.result });
    if (job.status === "done" || job.status === "failed") return job;
    await new Promise((r) => setTimeout(r, 1300));
  }
}

/** Normalize every intake shape to `{file, relPath}` pairs. The folder *picker*
 * (webkitdirectory) hands us Files carrying `webkitRelativePath`; the *drop*
 * path hands us already-paired {@link UploadItem}s from the entry traversal.
 * Both collapse to one list so the upload logic is identical for either. */
function toUploadItems(input: File[] | FileList | UploadItem[]): UploadItem[] {
  return Array.from(input as ArrayLike<File | UploadItem>).map((x) =>
    x instanceof File
      ? {
          file: x,
          relPath:
            (x as File & { webkitRelativePath?: string }).webkitRelativePath || undefined,
        }
      : x,
  );
}

export function useUpload() {
  return useCallback(async (input: File[] | FileList | UploadItem[]) => {
    const items = toUploadItems(input);
    const usable = items.filter((it) =>
      ACCEPTED.some((ext) => it.file.name.toLowerCase().endsWith(ext)),
    );
    for (const rejected of items.filter((it) => !usable.includes(it))) {
      toast.warning(`Skipped ${rejected.file.name}`, {
        description: "Unsupported type — try a document, office file, web page, or code file.",
      });
    }

    await Promise.all(
      usable.map(async ({ file, relPath }) => {
        try {
          // Folder import forwards the file's path relative to the chosen
          // folder so the server preserves the relative folders under Papers/.
          const { document, job_id, deduplicated } = await uploadDocument(
            file,
            relPath || undefined,
          );
          if (deduplicated || !job_id) {
            toast.info(`${document.title} is already in the atlas`);
            return;
          }
          useActivity.getState().upsert({
            id: job_id,
            kind: "ingest",
            title: document.title,
            docId: document.id,
            status: "queued",
          });
          toast(`Reading ${document.title}…`, {
            description: "Parsing structure, summarizing, embedding.",
          });
          const job = await trackJob(job_id, document.id);
          if (job.status === "done") {
            toast.success(`${document.title} joined the atlas`);
            await queryClient.invalidateQueries({ queryKey: keys.overview });
            await queryClient.invalidateQueries({ queryKey: keys.documents });
            // Refresh the file explorer so the imported file (and the folders it
            // brought with it) appears at once, not after the next 30s poll
            // (useVaultTree's refetchInterval; see hooks/queries.ts).
            await queryClient.invalidateQueries({ queryKey: keys.vault });
            // Bring the atlas forward and glide to the new document node.
            const overview = await queryClient.fetchQuery({ queryKey: keys.overview, queryFn: () => api.graphOverview() });
            const docNode = (overview as { document_nodes: { id: string; document_id: string | null }[] })
              .document_nodes.find((n) => n.document_id === document.id);
            if (docNode) {
              useTabs.getState().activate("graph");
              useGraph.getState().markFlash([docNode.id]);
              useGraph.getState().requestFocus(docNode.id);
            }
            // The one-call enrich (summary + concept connections) runs in the
            // background — refresh the atlas when it lands so the new concepts
            // and the links they form between documents appear.
            const enrichJobId = (job.result as { enrich_job_id?: string } | null)?.enrich_job_id;
            if (enrichJobId) {
              void trackJob(enrichJobId).then(() =>
                queryClient.invalidateQueries({ queryKey: keys.overview }),
              );
            }
          } else {
            toast.error(`Failed to ingest ${document.title}`, { description: job.error ?? "" });
          }
        } catch (err) {
          toast.error(`Upload failed: ${file.name}`, {
            description: err instanceof Error ? err.message : String(err),
          });
        }
      }),
    );
  }, []);
}
